import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { lociAmber } from '../../../theme/design-tokens';
import { cltAccentTokens } from '../../../tokens/colors';
/**
 * LOCI-branded coordinate cluster marker. Replaces stock map cluster pins with
 * a four-layer visual:
 *   1. Outer glow — pulsing teal blur (disabled under prefers-reduced-motion)
 *   2. Coordinate ring — dashed amber circle
 *   3. Constituent dots — small dots arranged radially, count = min(count, dotCount)
 *   4. Count chip — centered dark pill with the cluster's numeric count
 *
 * The component is map-library agnostic — consumers mount it into whatever DOM
 * node their map API provides for HTML markers.
 */
// LOCI design tokens — imported from @loci/ui SSoT. The atom can still be
// mounted outside ThemeProvider (e.g. via createRoot into a map marker
// container) because the imported constants resolve at bundle time.
const TEAL = cltAccentTokens.skylineGlow;
const AMBER = lociAmber;
const CHIP_BG = 'rgba(14, 15, 20, 0.92)';
const CHIP_TEXT = '#FFFFFF';
// Geometry — 48x48 viewBox, centered.
const VIEWBOX_SIZE = 48;
const CENTER = 24;
const GLOW_RADIUS = 22;
const RING_RADIUS = 18;
const DOT_ORBIT_RADIUS = 14;
const DOT_RADIUS = 1.8;
const CHIP_WIDTH = 22;
const CHIP_HEIGHT = 14;
const DEFAULT_DOT_CAP = 8;
const STYLE_ID = 'loci-cluster-node-keyframes';
const CLUSTER_KEYFRAMES = `
  @keyframes fs-cluster-glow-pulse {
    0%, 100% { opacity: 0.15; }
    50%      { opacity: 0.28; }
  }

  @keyframes fs-cluster-glow-pulse-selected {
    0%, 100% { opacity: 0.32; }
    50%      { opacity: 0.5; }
  }

  .fs-cluster-glow {
    animation: fs-cluster-glow-pulse 2s ease-in-out infinite;
    will-change: opacity;
  }

  .fs-cluster-glow--selected {
    animation: fs-cluster-glow-pulse-selected 2s ease-in-out infinite;
  }
`;
// React 18 ships useInsertionEffect; fall back to useLayoutEffect for older
// renderers and SSR to keep style injection timed before paint.
const useInsertionEffectFallback = React.useInsertionEffect ??
    React.useLayoutEffect;
const useClusterKeyframes = () => {
    useInsertionEffectFallback(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ID))
            return;
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = CLUSTER_KEYFRAMES;
        document.head.appendChild(style);
    }, []);
};
const useReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => typeof window !== 'undefined'
        ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
        : false);
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq)
            return;
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return prefersReducedMotion;
};
/**
 * Place `dotCount` points evenly around a circle of `radius` units. Index 0
 * sits at 12 o'clock and subsequent indices proceed clockwise. Mirrors the
 * polar formula used by `computeSpreadOffset` in `clusterUtils.ts` so the
 * visual dot layout matches the underlying max-zoom spread geometry.
 */
export const computeRadialDotPositions = (dotCount, radius) => {
    if (!Number.isFinite(dotCount) || dotCount <= 0)
        return [];
    if (dotCount === 1)
        return [{ x: 0, y: 0 }];
    const positions = [];
    for (let i = 0; i < dotCount; i += 1) {
        // angle = π/2 − (i/n) × 2π places index 0 at the top and walks clockwise
        // in SVG space (where y increases downward, so sin(angle) is inverted).
        const angle = Math.PI / 2 - (i / dotCount) * Math.PI * 2;
        positions.push({
            x: Math.cos(angle) * radius,
            y: -Math.sin(angle) * radius,
        });
    }
    return positions;
};
const clampDotCount = (count, cap) => {
    if (!Number.isFinite(count) || count <= 0)
        return 0;
    const normalized = Math.max(0, Math.floor(count));
    const maxCap = Math.max(0, Math.floor(cap));
    return Math.min(normalized, maxCap);
};
const defaultAriaLabel = (count) => {
    if (count <= 1)
        return `${count} event`;
    return `Cluster of ${count} events`;
};
/**
 * Fit the count display inside the 22px count chip without overflowing.
 * At fontSize 10 each character is ~6px wide, so 3+ digit counts visibly
 * spill out of the pill in dense urban datasets. We compress to a `k`
 * suffix once we reach four digits (e.g. `1.2k`) and shave the font size
 * for three-digit counts that still fit under compression.
 */
const formatCountChip = (count) => {
    if (count >= 10000)
        return { label: `${Math.floor(count / 1000)}k`, fontSize: 8.5 };
    if (count >= 1000)
        return { label: `${(count / 1000).toFixed(1)}k`, fontSize: 8 };
    if (count >= 100)
        return { label: String(count), fontSize: 8.5 };
    return { label: String(count), fontSize: 10 };
};
const ClusterNodeBase = ({ count, dotCount = DEFAULT_DOT_CAP, color = TEAL, selected = false, onClick, className, style, 'aria-label': ariaLabelProp, 'data-testid': dataTestId, }) => {
    useClusterKeyframes();
    const prefersReducedMotion = useReducedMotion();
    const safeCount = Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
    const visibleDotCount = clampDotCount(safeCount, dotCount);
    const shouldAnimate = !prefersReducedMotion;
    const dotPositions = useMemo(() => computeRadialDotPositions(visibleDotCount, DOT_ORBIT_RADIUS), [visibleDotCount]);
    const ariaLabel = ariaLabelProp ?? defaultAriaLabel(safeCount);
    const { label: chipLabel, fontSize: chipFontSize } = formatCountChip(safeCount);
    // Native `<button>` already synthesizes a click on Enter / Space when
    // focused, so there is no separate `onKeyDown` handler — a manual one
    // would double-fire on top of the native activation. `handleClick` covers
    // both mouse and keyboard paths.
    const handleClick = (event) => {
        if (!onClick)
            return;
        event.preventDefault();
        // Stop propagation so consumers embedding the node inside another
        // click-aware container (e.g. a map marker content element) don't
        // double-fire their own handler on top of this one.
        event.stopPropagation();
        onClick();
    };
    const interactive = typeof onClick === 'function';
    // Interactive variant carries the focus ring + cursor / transition
    // affordances; the presentational variant drops them so a non-interactive
    // `<div>` doesn't masquerade as a button.
    const rootClasses = [
        'fs-cluster-node',
        'relative inline-flex items-center justify-center',
        interactive
            ? 'bg-transparent border-0 p-0 cursor-pointer select-none transition-transform duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--loci-color-ring,#0e7b7b)] focus-visible:ring-offset-2 rounded-full'
            : 'select-none',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const rootStyle = {
        width: VIEWBOX_SIZE,
        height: VIEWBOX_SIZE,
        ...style,
    };
    const glowClassName = [
        'fs-cluster-glow',
        selected ? 'fs-cluster-glow--selected' : '',
        !shouldAnimate ? 'fs-cluster-glow--static' : '',
    ]
        .filter(Boolean)
        .join(' ');
    const svgContent = (_jsxs("svg", { width: VIEWBOX_SIZE, height: VIEWBOX_SIZE, viewBox: `0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`, "aria-hidden": "true", focusable: "false", style: { overflow: 'visible' }, children: [_jsx("circle", { "data-cluster-glow": "true", className: shouldAnimate ? glowClassName : undefined, cx: CENTER, cy: CENTER, r: GLOW_RADIUS, fill: TEAL, opacity: shouldAnimate ? undefined : selected ? 0.4 : 0.2, style: { filter: 'blur(10px)' } }), _jsx("circle", { "data-cluster-ring": "true", cx: CENTER, cy: CENTER, r: RING_RADIUS, fill: "none", stroke: AMBER, strokeWidth: 1.4, strokeDasharray: "3 3", opacity: selected ? 0.75 : 0.55 }), dotPositions.map((pos, idx) => (_jsx("circle", { "data-cluster-dot": idx, cx: CENTER + pos.x, cy: CENTER + pos.y, r: DOT_RADIUS, fill: color, opacity: 0.9 }, idx))), _jsxs("g", { "data-cluster-chip": "true", children: [_jsx("rect", { x: CENTER - CHIP_WIDTH / 2, y: CENTER - CHIP_HEIGHT / 2, width: CHIP_WIDTH, height: CHIP_HEIGHT, rx: CHIP_HEIGHT / 2, ry: CHIP_HEIGHT / 2, fill: CHIP_BG }), _jsx("text", { x: CENTER, y: CENTER, textAnchor: "middle", dominantBaseline: "central", fill: CHIP_TEXT, style: {
                            fontFamily: 'var(--loci-font-mono, "Space Grotesk", ui-monospace, SFMono-Regular, Menlo, monospace)',
                            fontWeight: 600,
                            fontSize: chipFontSize,
                            letterSpacing: '0.02em',
                        }, children: chipLabel })] })] }));
    if (interactive) {
        return (_jsx("button", { type: "button", onClick: handleClick, "aria-label": ariaLabel, "data-testid": dataTestId, "data-selected": selected ? 'true' : 'false', "data-count": safeCount, className: rootClasses, style: rootStyle, children: svgContent }));
    }
    // Presentational variant — no onClick means the node is purely decorative.
    // Render a non-interactive <div> hidden from assistive tech so screen
    // readers don't announce an inert control. Consumers that want the count
    // announced should either supply an `onClick` or wrap the node themselves.
    return (_jsx("div", { "aria-hidden": "true", "data-testid": dataTestId, "data-selected": selected ? 'true' : 'false', "data-count": safeCount, className: rootClasses, style: rootStyle, children: svgContent }));
};
export const ClusterNode = React.memo(ClusterNodeBase);
ClusterNode.displayName = 'ClusterNode';
